import { createFileRoute, redirect } from '@tanstack/react-router';
import { Box, Alert, AlertTitle } from '@mui/material';

// relative imports
import OAuthRedirectPage from '../../components/shared/authentication/oauth/OAuthRedirectPage';
import { OAUTH_SESSION_QUERY } from '../../data/apollo/queries';

export const Route = createFileRoute('/_public/oauth-callback/$providerId')({
  component: OAuthRedirectPage,
  errorComponent: ({ error }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Alert severity="error" sx={{ width: '90vw', marginTop: '3rem' }}>
        <AlertTitle>Auth Error</AlertTitle>
        {error?.message || 'An unknown error occurred'}
      </Alert>
    </Box>
  ),
  beforeLoad: async ({ params, search }) => {
    // check that necessary params are present
    if (!params.providerId) {
      throw new Error('Provider ID is required');
    }

    // this should exist if the user is redirected here from the oauth provider while using the Ledgible app
    const storedProviderString = sessionStorage.getItem(
      `${params.providerId}Auth`,
    );

    // Determine the request type based on whether we have stored provider state
    const requestType = storedProviderString ? 'ledgible' : 'api';

    return {
      requestType,
      providerId: params.providerId,
      searchParams: search,
    };
  },
  loader: async ({ context: { apolloClient, ...context } }) => {
    const { data } = await apolloClient.query({
      query: OAUTH_SESSION_QUERY,
      variables: {
        state: context.searchParams.state,
      },
      fetchPolicy: 'no-cache',
      context: { skipToken: true },
    });

    if (!data?.getOAuthSession) {
      throw new Error('No OAuth session found');
    }

    const { forwardingUri } = data.getOAuthSession;

    if (forwardingUri) {
      // Check if it's a custom URI scheme
      if (
        forwardingUri.includes('://') &&
        !forwardingUri.startsWith('http://') &&
        !forwardingUri.startsWith('https://')
      ) {
        // For custom URI schemes, we need to manually construct the URL with search params
        const searchParams = new URLSearchParams();
        Object.entries(context.searchParams).forEach(([key, value]) => {
          searchParams.append(key, value);
        });

        const queryString = searchParams.toString();
        const finalUri = queryString
          ? `${forwardingUri}?${queryString}`
          : forwardingUri;

        throw redirect({
          href: finalUri,
        });
      } else {
        // For web URLs, use the URL constructor
        const redirectUrl = new URL(forwardingUri);
        Object.entries(context.searchParams).forEach(([key, value]) => {
          redirectUrl.searchParams.append(key, value);
        });

        throw redirect({
          href: redirectUrl.toString(),
        });
      }
    }
  },
});
